import { useEffect, useRef, useCallback, useState } from "react";
import "./MapStyle.css";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useDispatch } from "react-redux";
import {
  setSingleProgramId,
  setActiveOnglet,
  addOpenProgram,
} from "../../../redux/actions/immobilierActions";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const MapView = ({
  locations = null,
  position = { lng: -122.4194, lat: 37.7749 },
}) => {
  const MARKER_IMAGE = "./assets/appart.png";
  const MARKER_SIZE = { width: "70px", height: "70px" };
  const MARKER_STYLE = { borderRadius: "5px" };
  const SELECTED_MARKER_STYLE = {
    border: "3px solid #FF385C",
    boxShadow: "0 0 10px rgba(0,0,0,0.3)",
  };
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const [prevLng, setPrevLng] = useState(null);
  const [prevLat, setPrevLat] = useState(null);
  const { lng, lat, image } = position || {};

  const initializeMap = useCallback((container, center, zoom) => {
    return new mapboxgl.Map({
      container,
      style: "mapbox://styles/mapbox/streets-v11",
      center,
      zoom,
    });
  }, []);

  const dispatch = useDispatch();
  const createMarker = useCallback(
    (location, map) => {
      const markerElement = document.createElement("img");
      const fallbackImage = "./assets/imgNotFound.webp";

      // Try to use the program image first, fallback to imgNotFound if not available, then to MARKER_IMAGE as last resort
      markerElement.src =
        location?.lots[0]?.images[0]?.origine_url || fallbackImage;

      // Handle image loading errors
      markerElement.onerror = () => {
        markerElement.src = fallbackImage;
        // If fallback also fails, use the default marker image
        markerElement.onerror = () => {
          markerElement.src = MARKER_IMAGE;
        };
      };
      markerElement.style.width = MARKER_SIZE.width;
      markerElement.style.height = MARKER_SIZE.height;
      markerElement.style.borderRadius = MARKER_STYLE.borderRadius;
      markerElement.style.cursor = "pointer";
      markerElement.id = `marker-${location.documentId}`;

      markerElement.addEventListener("click", (e) => {
        e.stopPropagation();
        dispatch(addOpenProgram(location.documentId));
        dispatch(setSingleProgramId(location.documentId));
        dispatch(setActiveOnglet(`programme-${location.documentId}`));
      });

      const marker = new mapboxgl.Marker({ element: markerElement })
        .setLngLat([location.longitude, location.latitude])
        .addTo(map);

      // Store marker reference for later updates
      markersRef.current[location.documentId] = {
        marker,
        element: markerElement,
        location,
      };

      return marker;
    },
    [dispatch]
  );

  // Initialize map only once
  useEffect(() => {
    if (!mapRef.current && mapContainerRef.current && locations) {
      mapRef.current = initializeMap(mapContainerRef.current, [lng, lat], 12);

      // Create all markers
      if (locations && locations.length > 0) {
        locations.forEach((location) => createMarker(location, mapRef.current));
      }
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markersRef.current = {};
      }
    };
  }, [initializeMap, createMarker, locations]);

  // Handle position changes separately
  useEffect(() => {
    // Only proceed if map is initialized and position has changed
    if (mapRef.current && (lng !== prevLng || lat !== prevLat)) {
      // Fly to the new position
      mapRef.current.flyTo({
        center: [lng, lat],
        zoom: 14,
        essential: true,
        duration: 4000,
      });

      // Update selected marker styles
      Object.values(markersRef.current).forEach(({ element, location }) => {
        // Reset all markers to default style
        element.style.border = "none";
        element.style.boxShadow = "none";

        // Highlight the selected marker
        if (location.longitude === lng && location.latitude === lat) {
          element.style.border = SELECTED_MARKER_STYLE.border;
          element.style.boxShadow = SELECTED_MARKER_STYLE.boxShadow;
        }
      });

      // Update previous position
      setPrevLng(lng);
      setPrevLat(lat);
    }
  }, [lng, lat, prevLng, prevLat]);

  return <div className="mapView radius5" ref={mapContainerRef} />;
};

export default MapView;
